import { useSnackbar } from "notistack";
import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
// @mui
import { alpha } from "@mui/material/styles";
import { Box, Divider, Typography, Stack, MenuItem } from "@mui/material";
// routes
import { PATH_DASHBOARD, PATH_AUTH } from "../../../routes/paths";
// hooks
import useAuth from "../../../hooks/useAuth";
import useIsMountedRef from "../../../hooks/useIsMountedRef";
import { useTranslation } from "react-i18next";
// components
import MyAvatar from "../../../components/MyAvatar";
import MenuPopover from "../../../components/MenuPopover";
import { IconButtonAnimate } from "../../../components/animate";
import useSocket from "../../..//hooks/useSocket";
import Iconify from "../../../components/Iconify";

// ----------------------------------------------------------------------

const MENU_OPTIONS = [
  {
    label: "home",
    linkTo: "/",
    icon: "solar:home-2-bold-duotone",
  },
  {
    label: "profile",
    linkTo: PATH_DASHBOARD.user.profile,
    icon: "solar:user-circle-bold-duotone",
  },
  {
    label: "account_setting",
    linkTo: PATH_DASHBOARD.user.account,
    icon: "solar:settings-bold-duotone",
  },
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const navigate = useNavigate();

  const { user, logout } = useAuth();
  const { closeWebSocket } = useSocket();

  const isMountedRef = useIsMountedRef();

  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(null);

  const { t } = useTranslation();

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      await closeWebSocket();
      navigate(PATH_AUTH.login, { replace: true });

      if (isMountedRef.current) {
        handleClose();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Unable to logout!", { variant: "error" });
    }
  };

  return (
    <>
      <IconButtonAnimate
        onClick={handleOpen}
        sx={{
          p: 0,
          ...(open && {
            "&:before": {
              zIndex: 1,
              content: "''",
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              position: "absolute",
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
            },
          }),
        }}
      >
        <MyAvatar photo={user?.photo} username={user?.username} />
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          p: 0,
          mt: 1.5,
          ml: 0.75,
          width: 320,
          borderRadius: 4,
          overflow: "visible",
          border: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
          boxShadow: "0 24px 54px rgba(15, 23, 42, 0.14)",
          "&:before": {
            content: '""',
            top: -7,
            right: 26,
            width: 14,
            height: 14,
            position: "absolute",
            borderTop: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
            borderLeft: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
            bgcolor: "background.paper",
            transform: "rotate(45deg)",
            borderTopLeftRadius: 2,
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 2,
            display: "flex",
            gap: 1.5,
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              p: 0.4,
              borderRadius: "50%",
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            }}
          >
            <MyAvatar photo={user?.photo} username={user?.username} sx={{ width: 52, height: 52 }} />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap>
              {(user?.first_name || "") + " " + (user?.last_name || "")}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
              @{user?.username}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderStyle: "dashed" }} />

        <Stack sx={{ p: 1.25, gap: 0.5 }}>
          {MENU_OPTIONS.map((option) => (
            <MenuItem
              key={option.label}
              to={option.linkTo}
              component={RouterLink}
              onClick={handleClose}
              sx={{
                px: 1.25,
                py: 1.15,
                borderRadius: 2.5,
                typography: "body1",
                fontWeight: 600,
                color: "text.primary",
                gap: 1.25,
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: 2,
                  color: "primary.main",
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  flexShrink: 0,
                }}
              >
                <Iconify icon={option.icon} width={20} height={20} />
              </Box>
              {t("params." + option.label)}
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: "dashed" }} />

        <MenuItem
          onClick={handleLogout}
          sx={{
            m: 1.25,
            px: 1.25,
            py: 1.15,
            borderRadius: 2.5,
            typography: "body1",
            fontWeight: 700,
            color: "error.main",
            gap: 1.25,
            bgcolor: (theme) => alpha(theme.palette.error.main, 0.06),
          }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              display: "grid",
              placeItems: "center",
              borderRadius: 2,
              color: "error.main",
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
              flexShrink: 0,
            }}
          >
            <Iconify icon="solar:logout-2-bold-duotone" width={20} height={20} />
          </Box>
          {t("params.logout")}
        </MenuItem>
      </MenuPopover>
    </>
  );
}
